import { useAppStore, type Sheet } from '../store/app'
import { HouseholdPickerSheet } from './household/HouseholdPickerSheet'
import { DeveloperSheet } from './account/DeveloperSheet'
import { PaywallSheet } from './paywall/PaywallSheet'
import { ResourceConnectionsSheet } from './connections/ResourceConnectionsSheet'
import { HostsSheet } from './hosts/HostsSheet'
import { DevicesSheet } from './devices/DevicesSheet'
import { VoiceDevicesSheet } from './devices/VoiceDevicesSheet'
import { PositionsSheet } from './positions/PositionsSheet'
import { WorkingDirectoriesSheet } from './working_directories/WorkingDirectoriesSheet'
import { OrganizationMembersSheet } from './members/OrganizationMembersSheet'
import { AgentJobDescriptionsSheet } from './job_descriptions/AgentJobDescriptionsSheet'
import { SecretsSheet } from './secrets/SecretsSheet'
import { PluginInstallsSheet } from './plugin_installs/PluginInstallsSheet'
import { RulesSheet } from './rules/RulesSheet'
import { ApproversSheet } from './approvers/ApproversSheet'
import { NotificationPreferencesSheet } from './notification_preferences/NotificationPreferencesSheet'
import { DeleteAccountSheet } from './account/DeleteAccountSheet'
import { DeleteHouseholdSheet } from './household/DeleteHouseholdSheet'
import { AttentionSheet } from './attention/AttentionSheet'
import { OpenRequestsSheet } from './open_requests/OpenRequestsSheet'
import { DelegationsSheet } from './delegations/DelegationsSheet'
import { ChatSettingsSheet } from './chat/ChatSettingsSheet'
import { CreateChatRoomSheet } from './chat/CreateChatRoomSheet'
import { CreateEntitySheet } from './CreateEntitySheet'
import { InvitationsSheet } from './invitations/InvitationsSheet'
import { BlueprintPickerSheet } from './agents/BlueprintPickerSheet'
import { ConnectAccountWizard } from './connections/ConnectAccountWizard'
import './SheetStack.css'

/// Modal sheet stack — each entry rises from the bottom and covers ~92%
/// of the viewport. Topmost sheet is interactive; older sheets beneath
/// would be visible if the top is transparent but in practice aren't
/// because each sheet is opaque. Pop via the Done button (top-right) or
/// tap on the backdrop above the sheet.
export function SheetStack() {
  const sheets = useAppStore((s) => s.sheets)
  const popSheet = useAppStore((s) => s.popSheet)
  if (sheets.length === 0) return null

  return (
    <>
      {sheets.map((sheet, i) => (
        <div
          key={`${sheet.kind}-${i}`}
          className="sheet"
          role="dialog"
          aria-modal="true"
          style={{ zIndex: 300 + i }}
          onClick={(e) => {
            // Only pop when clicking the backdrop, and only for the top
            // sheet. Clicks on child sheets shouldn't pop anything since
            // the overlay covers everything beneath.
            if (e.target === e.currentTarget && i === sheets.length - 1) {
              popSheet()
            }
          }}
        >
          <div className="sheet__body">
            {/* Wizard sheets use their own nav header — skip the standard sheet header */}
            {sheet.kind !== 'create_chat_room' && sheet.kind !== 'create_entity' && sheet.kind !== 'connect_account' && sheet.kind !== 'blueprint_picker' && (
              <header className="sheet__header">
                <button className="sheet__close" onClick={popSheet}>
                  Done
                </button>
                <div className="sheet__title">{labelFor(sheet)}</div>
                <div className="sheet__close-spacer" />
              </header>
            )}
            <div className="sheet__content">
              {sheet.kind === 'create_entity' && <CreateEntitySheet />}
              {sheet.kind === 'connect_account' && <ConnectAccountWizard />}
              {sheet.kind === 'blueprint_picker' && <BlueprintPickerSheet />}
              {sheet.kind === 'create_chat_room' && <CreateChatRoomSheet />}
              {sheet.kind === 'household_picker' && <HouseholdPickerSheet initialMode={sheet.initialMode} />}
              {sheet.kind === 'invitations' && <InvitationsSheet />}
              {sheet.kind === 'voice_devices' && <VoiceDevicesSheet />}
              {sheet.kind === 'developer' && <DeveloperSheet />}
              {sheet.kind === 'paywall' && <PaywallSheet upgradeOnly={sheet.upgradeOnly} />}
              {sheet.kind === 'resource_connections_debug' && <ResourceConnectionsSheet />}
              {sheet.kind === 'hosts_debug' && <HostsSheet />}
              {sheet.kind === 'devices_debug' && <DevicesSheet />}
              {sheet.kind === 'positions_debug' && <PositionsSheet />}
              {sheet.kind === 'working_directories_debug' && <WorkingDirectoriesSheet />}
              {sheet.kind === 'organization_members_debug' && <OrganizationMembersSheet />}
              {sheet.kind === 'agent_job_descriptions_debug' && <AgentJobDescriptionsSheet />}
              {sheet.kind === 'secrets_debug' && <SecretsSheet />}
              {sheet.kind === 'plugin_installs_debug' && <PluginInstallsSheet />}
              {sheet.kind === 'rules_debug' && <RulesSheet />}
              {sheet.kind === 'approvers' && <ApproversSheet />}
              {sheet.kind === 'notification_preferences' && <NotificationPreferencesSheet />}
              {sheet.kind === 'delete_account' && <DeleteAccountSheet />}
              {sheet.kind === 'delete_household' && (
                <DeleteHouseholdSheet organizationID={sheet.organizationID} />
              )}
              {sheet.kind === 'attention' && <AttentionSheet />}
              {sheet.kind === 'open_requests' && <OpenRequestsSheet />}
              {sheet.kind === 'delegations' && <DelegationsSheet />}
              {sheet.kind === 'chat_settings' && (
                <ChatSettingsSheet roomID={sheet.roomID} roomName={sheet.roomName} />
              )}
            </div>
          </div>
        </div>
      ))}
    </>
  )
}

function labelFor(sheet: Sheet): string {
  if (sheet.kind === 'household_picker') {
    return 'Switch Household'
  }
  switch (sheet.kind) {
    case 'invitations':
      return 'Invitations'
    case 'voice_devices':
      return 'Voice Devices'
    case 'paywall':
      return 'Hearth Pro'
    case 'developer':
      return 'Developer'
    case 'resource_connections_debug':
      return 'Resource Connections'
    case 'hosts_debug':
      return 'Hosts'
    case 'devices_debug':
      return 'Devices'
    case 'positions_debug':
      return 'Positions'
    case 'working_directories_debug':
      return 'Working Directories'
    case 'organization_members_debug':
      return 'Members'
    case 'agent_job_descriptions_debug':
      return 'Job Descriptions'
    case 'secrets_debug':
      return 'Secrets'
    case 'plugin_installs_debug':
      return 'Installed Plugins'
    case 'rules_debug':
      return 'Rules'
    case 'approvers':
      return 'Approvers'
    case 'notification_preferences':
      return 'Notification Preferences'
    case 'delete_account':
      return 'Delete Account'
    case 'delete_household':
      return 'Delete Household'
    case 'attention':
      return 'Attention'
    case 'open_requests':
      return 'Open Requests'
    case 'delegations':
      return 'Delegate Approval'
    case 'chat_settings':
      return 'Chat Settings'
    case 'create_chat_room':
      return 'New Chat Room'
    case 'create_entity':
      return 'Create New'
    default:
      return ''
  }
}
